import { useEffect, useState } from 'react'
import { Range } from 'react-range'

import styles from './FormSlider.module.css'

export type FormSliderProps = {
  name: string
  label?: string
  isRequired?: boolean
  showRequired?: boolean
  min?: number
  max?: number
  step?: number
  value?: number
  defaultValue?: number
  disabled?: boolean
  prefix?: string
  onChange?: (value: number) => void
}

// Add a helper function to format numbers
const formatValue = (value: number, prefix?: string) => {
  if (value >= 1000000) {
    return `${prefix || ''}${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${prefix || ''}${(value / 1000).toFixed(1)}K`
  }
  return `${prefix || ''}${value.toFixed(0)}`
}

export const PhnxFormSlider: React.FC<FormSliderProps> = ({
  name,
  label,
  isRequired = false,
  showRequired = true,
  min = 0,
  max = 100,
  step = 1,
  value,
  defaultValue = 50,
  disabled = false,
  prefix,
  onChange,
}) => {
  const [currentValue, setCurrentValue] = useState([value ?? defaultValue])

  useEffect(() => {
    if (!disabled && value !== undefined) {
      setCurrentValue([value])
    }
  }, [value, disabled])

  return (
    <div className={`${styles.formSlider} ${disabled ? styles.disabled : ''}`}>
      {label && (
        <label htmlFor={name}>
          {label} {isRequired && showRequired && '*'}
        </label>
      )}
      <div className={styles.sliderContainer}>
        <Range
          step={step}
          min={min}
          max={max}
          values={currentValue}
          disabled={disabled}
          onChange={(newValues) => {
            if (!disabled) {
              setCurrentValue(newValues)
              onChange?.(newValues[0])
            }
          }}
          renderTrack={({ props, children }) => (
            <div
              role="slider"
              aria-valuemin={min}
              aria-valuemax={max}
              aria-valuenow={currentValue[0]}
              onMouseDown={props.onMouseDown}
              onTouchStart={props.onTouchStart}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft') {
                  onChange?.(Math.max(currentValue[0] - step, min))
                }
                if (e.key === 'ArrowRight') {
                  onChange?.(Math.min(currentValue[0] + step, max))
                }
              }}
              tabIndex={0}
              style={{
                ...props.style,
                height: '36px',
                display: 'flex',
                width: '100%',
              }}
            >
              <div
                ref={props.ref}
                className={styles.sliderTrack}
                style={{
                  background: `linear-gradient(to right,
                    #d43d27 0%,
                    #d43d27 ${((currentValue[0] - min) / (max - min)) * 100}%,
                    #e5e7eb ${((currentValue[0] - min) / (max - min)) * 100}%)`,
                }}
              >
                {children}
              </div>
            </div>
          )}
          renderThumb={({ props }) => {
            const { key, ...otherProps } = props
            return (
              <div key={key} {...otherProps} className={styles.sliderThumb}>
                <div className={styles.sliderValue}>{formatValue(currentValue[0], prefix)}</div>
              </div>
            )
          }}
        />
        <div className={styles.sliderLabels}>
          <span>{formatValue(min, prefix)}</span>
          <span>{formatValue(max, prefix)}</span>
        </div>
      </div>
      <input type="hidden" name={name} value={currentValue[0]} required={isRequired} />
    </div>
  )
}
